import { useEffect } from 'react';
import { config } from '@safermark/shared';

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

export function GoogleAnalytics() {
  useEffect(() => {
    // Only initialize if analytics is enabled and we have a tracking ID
    if (!config.current.analytics.enabled || !config.current.analytics.googleAnalyticsId) {
      return;
    }

    const trackingId = config.current.analytics.googleAnalyticsId;

    // Create script tag for Google Analytics
    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${trackingId}`;
    script.async = true;
    document.head.appendChild(script);

    // Initialize dataLayer and gtag function
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag(...args: any[]) {
      window.dataLayer.push(args);
    };

    // Configure Google Analytics
    window.gtag('js', new Date());
    window.gtag('config', trackingId, {
      // Privacy-focused configuration
      anonymize_ip: true,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
    });

    // Cleanup function to remove script on unmount
    return () => {
      const existingScript = document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${trackingId}"]`);
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  // This component doesn't render anything
  return null;
}

// Utility function to track custom events
export function trackEvent(eventName: string, parameters?: Record<string, any>) {
  if (!config.current.analytics.enabled || typeof window.gtag !== 'function') {
    return;
  }

  window.gtag('event', eventName, {
    custom_parameter: true,
    ...parameters,
  });
}

// Utility function to track page views
export function trackPageView(pagePath: string, pageTitle?: string) {
  if (!config.current.analytics.enabled || typeof window.gtag !== 'function' || !config.current.analytics.googleAnalyticsId) {
    return;
  }

  window.gtag('config', config.current.analytics.googleAnalyticsId, {
    page_path: pagePath,
    page_title: pageTitle,
  });
}